import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { Step } from "react-joyride";
import { useSetState } from "react-use";
import { ProgressBar } from "src/components/progressbar";
import Typography from "src/components/typography";

interface AppState {
	run: boolean;
	stepIndex: number;
	steps: Step[];
	tourActive: boolean;
}

const appState: AppState = {
	run: false,
	stepIndex: 0,
	steps: [],
	tourActive: false,
};

const AppContext = createContext({
	state: appState,
	setState: () => undefined,
	id: 0,
	setDemoTripId: () => undefined,
});
AppContext.displayName = "AppContext";

export function AppProvider(props: any) {
	const [state, setState] = useSetState(appState);
	const [id, setDemoTripId] = useState(0);
	const isShownTour = localStorage.getItem("isShownTour") === "true";
	// Define steps when id changes and is not default (0)
	useEffect(() => {
		if (id !== 0 && !isShownTour) {
			setState({
				steps: [
					{
						target: "#manage-loads",
						content: (
							<div className="flex text-left justify-center flex-col gap-y-8 ">
								<Typography variant="h6" className="text-[#14B8A6] text-center">
									Start the demo 🚀
								</Typography>
								<div className="text-black mt-4 flex flex-col ">
									<Typography variant="bodyXM">Tap Load Management to explore a sample trip.</Typography>
									<Typography variant="bodyXM">
										📦 Loads are pre-set, but you'll get to see the final 3D truck layout.
									</Typography>
									<ProgressBar childrenClassName="bg-[#14B8A6]" className="h-2 my-4 " value={20} />
									<Typography variant="bodyXM" className="text-[#14B8A6]">
										1/5
									</Typography>
								</div>
							</div>
						),
						data: {
							next: `/dashboard/load-management/${id}`,
						},
						disableScrolling: false,
						placement: "bottom",
						disableBeacon: true,
					},
					{
						target: "#load-management-page",
						content: (
							<div className="flex text-left justify-center flex-col gap-y-8`">
								<Typography variant="h6" className="text-[#14B8A6] text-center">
									Load Management 📦
								</Typography>
								<div className="text-black mt-4 flex flex-col ">
									<Typography variant="bodyXM">
										🤖 AI picks the best from your loads for an optimized trip.
									</Typography>
									<Typography variant="bodyXM">
										In this demo, they're auto-assigned to a default trip.
									</Typography>
									<ProgressBar childrenClassName="bg-[#14B8A6]" className="h-2 my-4 " value={40} />
									<Typography variant="bodyXM" className="text-[#14B8A6]">
										2/5
									</Typography>
								</div>
							</div>
						),
						data: {
							previous: `/dashboard`,
						},
						disableBeacon: true,
					},
					{
						target: "#add-load-button",
						content: (
							<div className="flex text-left justify-center flex-col gap-y-8`">
								<Typography variant="h6" className="text-[#14B8A6] text-center">
									See your truck in 3D 🚚
								</Typography>
								<div className="text-black mt-4 flex flex-col ">
									<Typography variant="bodyXM">
										Tap 3D to view how loads are packed — optimized, from every angle.
									</Typography>
									<ProgressBar childrenClassName="bg-[#14B8A6]" className="h-2 my-4 " value={60} />
									<Typography variant="bodyXM" className="text-[#14B8A6]">
										3/5
									</Typography>
								</div>
							</div>
						),
						data: {
							next: `/dashboard/truck-visual?tripId=${id}`,
						},
						disableBeacon: true,
					},
					{
						target: "#view-loads",
						content: (
							<div className="flex text-left justify-center flex-col gap-y-8`">
								<Typography variant="h6" className="text-[#14B8A6] text-center">
									Explore your truck in 3D 🧭🚚
								</Typography>
								<div className="text-black mt-4 flex flex-col ">
									<Typography variant="bodyXM">
										Drag to rotate and zoom your truck.See how loads fit and where space is saved. ↩️ When
										you’re done, tap Back to return.
									</Typography>
									<ProgressBar childrenClassName="bg-[#14B8A6]" className="h-2 my-4 " value={80} />
									<Typography variant="bodyXM" className="text-[#14B8A6]">
										4/5
									</Typography>
								</div>
							</div>
						),
						data: {
							previous: `/dashboard/load-management/${id}`,
						},
						placement: "top",
						disableBeacon: true,
					},
					{
						target: "#finish-demo",
						locale: {
							back: "Exit Demo",
							last: "Create My Trip",
						},
						content: (
							<>
								<div className="flex text-left justify-center flex-col gap-y-8`">
									<Typography variant="h6" className="text-[#14B8A6] text-center">
										🚀 Demo complete!
									</Typography>
									<div className="text-black mt-4 flex flex-col ">
										<Typography variant="bodyXM">Explore your truck in 3D 🧭🚚</Typography>
										<Typography variant="bodyXM">
											You’ve seen how loads are optimized and packed in 3D.
										</Typography>
										<Typography variant="bodyXM">
											Now create your own trip to start using Load Logic Prime.
										</Typography>
										<ProgressBar childrenClassName="bg-[#14B8A6]" className="h-2 my-4 " value={100} />
										<Typography variant="bodyXM" className="text-[#14B8A6]">
											5/5
										</Typography>
									</div>
								</div>
							</>
						),
						data: {
							previous: `/dashboard`,
							next: `/dashboard/trip/add`,
						},
						placement: "center",
						disableBeacon: true,
					},
				],
				tourActive: true,
			});
		}
	}, [id, setState, isShownTour]);

	const value = useMemo(
		() => ({
			state,
			setState,
			id,
			setDemoTripId,
		}),
		[setState, state, id, setDemoTripId],
	);
	return <AppContext.Provider value={value} {...props} />;
}

export function useAppContext(): {
	setState: (patch: Partial<AppState> | ((previousState: AppState) => Partial<AppState>)) => void;
	state: AppState;
	id: number;
	setDemoTripId: (id: number) => void;
} {
	const context = useContext(AppContext);

	if (!context) {
		throw new Error("useAppContext must be used within a AppProvider");
	}

	return context;
}
